<template>
  <div class="space-y-3">
    <TransitionGroup name="family-list" tag="div" class="space-y-3">
      <div v-for="family in displayedFamilies" :key="family.family_name"
        class="p-3 rounded-xl cursor-pointer family-item"
        @click="navigateToFamily(family.family_name)">

        <div class="space-y-2">
            <!-- Header : nom famille + métriques -->
            <div class="flex items-start justify-between gap-2">
              <div class="min-w-0 flex-1">
                <h3
                  class="font-semibold text-foreground text-sm leading-tight truncate">
                  {{ family.family_name }}</h3>
                <p class="text-xs text-muted-foreground">{{ family.products_count }} {{
                  family.products_count > 1 ?
                    t('report.families_insights.product_plural') :
                    t('report.families_insights.product_singular') }}</p>
              </div>

              <div class="flex-shrink-0 flex items-center gap-2">
                <div class="flex flex-col items-end">
                  <span class="font-bold text-foreground text-sm">{{
                    formatCurrency(family.tax_data[taxRule]) }}</span>
                  <span v-if="family.comparison" class="text-xs font-medium"
                    :class="getEvolutionClass(family.comparison.tax_data_change_pct[taxRule])">
                    {{
                      formatEvolution(family.comparison.tax_data_change_pct[taxRule])
                    }}
                  </span>
                </div>
                <div class="w-4 h-4 text-muted-foreground/60">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round"
                      stroke-width="2" d="M9 5l7 7-7 7"></path>
                  </svg>
                </div>
              </div>
            </div>

            <!-- Métriques compactes -->
            <div
              class="flex items-center justify-between text-xs text-muted-foreground">
              <span>{{ family.total_lines }} {{
                t('report.families_insights.sales') }} • {{
                  getAverageBasket(family)
                }}{{ t('report.families_insights.per_sale') }}</span>
              <span>{{ getMarketShare(family.tax_data[taxRule]) }}</span>
            </div>

            <!-- Barre de progression -->
            <div class="flex items-center gap-2">
              <div
                class="flex-1 h-1.5 progress-track rounded-full overflow-hidden">
                <div
                  class="h-full progress-fill rounded-full transition-all duration-300"
                  :style="{ width: getProgressWidth(family.tax_data[taxRule]) + '%' }">
                </div>
              </div>
            </div>
          </div>
      </div>
    </TransitionGroup>

    <!-- Bouton voir plus/moins -->
    <div v-if="familiesData && familiesData.length > defaultLimit"
      class="text-center pt-3">
      <button @click="toggleExpanded"
        class="text-sm link-cyan font-medium flex items-center gap-1 mx-auto">
        <span v-if="!isExpanded">{{
          t('report.families_insights.show_all_families') }} ({{
            familiesData.length }})</span>
        <span v-else>{{ t('report.families_insights.reduce') }}</span>
        <svg class="w-4 h-4 transition-transform duration-200"
          :class="{ 'rotate-180': isExpanded }" fill="none"
          stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M19 9l-7 7-7-7"></path>
        </svg>
      </button>
    </div>

    <!-- Message si pas de données -->
    <div v-if="!familiesData || familiesData.length === 0"
      class="text-center text-muted-foreground py-8">
      {{ t('report.families_insights.no_families') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { ProductsReportResponse } from '../responses'
import { useSalesReportsStore } from '@/stores/salesReports'
import { formatCurrencyForCenter } from '@/utils/Languages'
import { Center } from '@/user'

interface Props {
  productsReport: ProductsReportResponse | null
  center: Center
  reportId: string
}

const emit = defineEmits<{
  'navigate-to-family': [familyName: string]
}>()

const salesReportsStore = useSalesReportsStore()
const props = defineProps<Props>()
const { t } = useI18n()
const report = computed(() => {
  return salesReportsStore.getReportForCenterAndComponent(props.center.id, props.reportId)
})
// État pour l'expansion
const isExpanded = ref(false)
const defaultLimit = 5

const taxRule = computed(() => {
  return report.value?.getTaxKey() || 'excluded_tax'
})

const familiesData = computed(() => {
  return props.productsReport?.families_summary || []
})

const displayedFamilies = computed(() => {
  if (isExpanded.value || familiesData.value.length <= defaultLimit) {
    return familiesData.value
  }
  return familiesData.value.slice(0, defaultLimit)
})

const totalRevenue = computed(() => {
  return props.productsReport?.period_summary?.total_revenue[taxRule.value] || 0
})

const maxRevenue = computed(() => {
  if (!familiesData.value || familiesData.value.length === 0) return 0
  return Math.max(...familiesData.value.map(f => f.tax_data[taxRule.value]))
})

// Toggle expansion
const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value
}

// Navigation vers la page de détail de la famille
const navigateToFamily = (familyName: string) => {
  emit('navigate-to-family', familyName)
}

// Part de marché de la famille
const getMarketShare = (familyRevenue: number): string => {
  if (totalRevenue.value === 0) return '0%'
  const share = (familyRevenue / totalRevenue.value) * 100
  return `${Math.round(share)}%`
}

// Largeur de la barre de progression
const getProgressWidth = (revenue: number): number => {
  if (maxRevenue.value === 0) return 0
  return Math.round((revenue / maxRevenue.value) * 100)
}

// Panier moyen de la famille
const getAverageBasket = (family: any): string => {
  if (family.total_lines === 0) return formatCurrency(0)
  const averageBasket = family.tax_data[taxRule.value] / family.total_lines
  return formatCurrency(averageBasket)
}

// Formatage de l'évolution en pourcentage
const formatEvolution = (percentage: number): string => {
  const sign = percentage > 0 ? '+' : ''
  return `${sign}${percentage.toFixed(1)}%`
}

// Classe CSS selon l'évolution
const getEvolutionClass = (percentage: number): string => {
  if (percentage > 0) return 'text-green-600'
  if (percentage < 0) return 'text-red-600'
  return 'text-muted-foreground'
}

// Formatage monétaire
const formatCurrency = (amount: number): string => {
  return formatCurrencyForCenter(amount, props.center)
}
</script>

<style scoped>
/* Design system tokens */
.family-item {
  border: 1px solid var(--border-medium);
  border-radius: var(--radius-card);
  transition: border-color 0.15s ease;
}

.family-item:hover {
  background: rgba(0, 0, 0, 0.02);
  border-color: var(--cyan-border-hover);
}

:is(.dark *).family-item:hover {
  background: rgba(255, 255, 255, 0.02);
}

.progress-track {
  background: var(--border-subtle);
}

.progress-fill {
  background: var(--apex-cyan);
}

.link-cyan {
  color: var(--apex-cyan);
  transition: opacity 0.15s ease;
}

.link-cyan:hover {
  opacity: 0.75;
}

/* Animations pour l'expansion de la liste */
.family-list-enter-active,
.family-list-leave-active {
  transition: all 0.3s ease;
}

.family-list-enter-from {
  opacity: 0;
  transform: translateY(-10px);
  max-height: 0;
  overflow: hidden;
}

.family-list-leave-to {
  opacity: 0;
  transform: translateY(-10px);
  max-height: 0;
  overflow: hidden;
}

.family-list-move {
  transition: transform 0.3s ease;
}

/* Animation pour empêcher le saut lors de l'ajout/suppression */
.family-list-enter-active {
  animation: slideDown 0.3s ease forwards;
}

.family-list-leave-active {
  animation: slideUp 0.3s ease forwards;
}

@keyframes slideDown {
  from {
    opacity: 0;
    max-height: 0;
    margin-bottom: 0;
    padding-top: 0;
    padding-bottom: 0;
  }

  to {
    opacity: 1;
    max-height: 200px;
    margin-bottom: 0.75rem;
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
  }
}

@keyframes slideUp {
  from {
    opacity: 1;
    max-height: 200px;
    margin-bottom: 0.75rem;
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
  }

  to {
    opacity: 0;
    max-height: 0;
    margin-bottom: 0;
    padding-top: 0;
    padding-bottom: 0;
  }
}
</style>